// ---- Weapon animation archetypes ----
// Animation + shared grip (hold override / stances / attack clip) is keyed by
// ARCHETYPE, not by individual item type. Every iron/steel/rusty one-hand sword
// shares one-hand-sword; every bow shares bow. Per-item fields stay for combat
// stats, the mesh model, and model-specific hold.hilt seating.
//
// content/anim/clips.json `archetypes.<id>` holds the pose data; this module is
// the closed id set + human labels used by schema, validate, studio, and load.

/** Closed set of weapon animation archetypes. Adding one = entry here + a block
 *  in content/anim/clips.json + at least one item pointing at it. */
export const WEAPON_ARCHETYPE_IDS = [
  "one-hand-sword",
  "two-hand-sword",
  "polearm",
  "battleaxe",
  "hatchet",
  "bow",
] as const;

export type WeaponArchetypeId = (typeof WEAPON_ARCHETYPE_IDS)[number];

export type WeaponArchetypeMeta = {
  id: WeaponArchetypeId;
  /** Studio / picker label. */
  label: string;
  icon: string;
  /** Expected combat grip — items should match (validate warns on mismatch). */
  grip: "one-hand" | "two-hand";
  /** Expected combat style — items should match (validate warns on mismatch). */
  style: "swing" | "chop" | "thrust" | "draw";
};

/** Authoritative archetype catalog — order is the studio picker order. */
export const WEAPON_ARCHETYPES: Record<WeaponArchetypeId, WeaponArchetypeMeta> = {
  "one-hand-sword": {
    id: "one-hand-sword",
    label: "One-Handed Sword",
    icon: "🗡️",
    grip: "one-hand",
    style: "swing",
  },
  "two-hand-sword": {
    id: "two-hand-sword",
    label: "Two-Handed Sword",
    icon: "⚔️",
    grip: "two-hand",
    style: "swing",
  },
  polearm: {
    id: "polearm",
    label: "Polearm",
    icon: "🔱",
    grip: "two-hand",
    style: "thrust",
  },
  battleaxe: {
    id: "battleaxe",
    label: "Battleaxe",
    icon: "🪓",
    grip: "one-hand",
    style: "chop",
  },
  hatchet: {
    id: "hatchet",
    label: "Hatchet",
    icon: "🪓",
    grip: "one-hand",
    style: "chop",
  },
  bow: {
    id: "bow",
    label: "Bow",
    icon: "🏹",
    grip: "two-hand",
    style: "draw",
  },
};

export function isWeaponArchetypeId(s: string): s is WeaponArchetypeId {
  return (WEAPON_ARCHETYPE_IDS as readonly string[]).includes(s);
}

/** Display string for studio headers: "🗡️ One-Handed Sword". Tolerant of a raw
 *  string (studio pickers hand string-typed option ids) — an unknown id renders
 *  as-is. */
export function archetypeLabel(id: WeaponArchetypeId | string): string {
  if (!isWeaponArchetypeId(id)) return id;
  const m = WEAPON_ARCHETYPES[id];
  return `${m.icon} ${m.label}`;
}
